import React, { useRef, useState } from 'react';
import { ArrowUpRight, BriefcaseBusiness, Calculator, Scale } from 'lucide-react';
import { Section, SectionHeading } from '../ui/Section';
const groups = [{
  id: 'core',
  label: 'Core technology',
  icon: BriefcaseBusiness,
  intro: 'Global platforms used across modern professional firms.',
  items: [['Microsoft 365', 'Collaboration, identity and productivity'], ['WatchGuard', 'Network security'], ['Dell', 'Business hardware'], ['Veeam', 'Backup and recovery'], ['VMware', 'Virtual infrastructure']]
}, {
  id: 'legal',
  label: 'Legal applications',
  icon: Scale,
  intro: 'Practice, workflow and client-intake systems used by legal firms.',
  items: [['LexisNexis', 'Legal research and practice systems'], ['FilePro', 'Practice and document management'], ['Settify', 'Digital client intake'], ['Lawdocs', 'Document workflows'], ['Actionstep', 'Cloud practice management']]
}, {
  id: 'accounting',
  label: 'Accounting applications',
  icon: Calculator,
  intro: 'ERP, audit and compliance platforms used by accounting firms.',
  items: [['MYOB Advanced', 'Cloud ERP'], ['MYOB EXO', 'Business and finance management'], ['CaseWare', 'Audit and financial reporting'], ['BGL Simple Fund 360', 'SMSF administration'], ['Microsoft 365', 'Productivity and collaboration']]
}];
export function TechnologyEcosystem() {
  const [activeIndex, setActiveIndex] = useState(0);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const active = groups[activeIndex];
  const moveFocus = (index: number) => {
    const next = (index + groups.length) % groups.length;
    setActiveIndex(next);
    buttons.current[next]?.focus();
  };
  return <Section id="technology-ecosystem" className="bg-white" ariaLabel="Technology and software ecosystem" data-id="element-387" data-mp="34lmnse759qnd">
      <div className="grid gap-8 lg:grid-cols-2 lg:items-end" data-id="element-388" data-mp="2zk5qstrs6oc2">
        <SectionHeading eyebrow="Technology ecosystem" title="The applications your firm relies on should not become someone else’s problem." body="CT Group works across global technology platforms and the specialist systems used by legal and accounting firms." data-id="element-389" data-mp="312nvasnyy2ry" />
        <div className="flex flex-wrap gap-2 lg:justify-end" role="tablist" aria-label="Technology categories" data-id="element-390" data-mp="2s4k7fppigtwz">
          {groups.map((group, index) => {
          const selected = index === activeIndex;
          return <button key={group.id} ref={element => {
            buttons.current[index] = element;
          }} id={`ecosystem-tab-${group.id}`} type="button" role="tab" aria-selected={selected} aria-controls="ecosystem-panel" tabIndex={selected ? 0 : -1} onClick={() => setActiveIndex(index)} onKeyDown={event => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
              event.preventDefault();
              moveFocus(index + 1);
            }
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
              event.preventDefault();
              moveFocus(index - 1);
            }
            if (event.key === 'Home') {
              event.preventDefault();
              moveFocus(0);
            }
            if (event.key === 'End') {
              event.preventDefault();
              moveFocus(groups.length - 1);
            }
          }} className={`inline-flex min-h-[46px] items-center gap-2 border px-4 text-[13px] font-bold transition-[background-color,color,border-color] duration-200 ease-out ${selected ? 'border-navy bg-navy text-white' : 'border-border bg-white text-ink hover:border-ct-cyan'}`} data-id="element-391" data-mp="2n3o3xd92n9s3" data-mpi={index}>
                <group.icon className="h-4 w-4" aria-hidden data-id="element-392" data-mp="28hh46i0oxsyd" />{group.label}
              </button>;
        })}
        </div>
      </div>

      <div id="ecosystem-panel" role="tabpanel" aria-labelledby={`ecosystem-tab-${active.id}`} className="mt-9 grid border-y border-border lg:grid-cols-[0.65fr_1.35fr]" data-id="element-393" data-mp="2mdujbkhr5i9o">
        <div className="flex flex-col justify-center border-b border-border bg-surface-2 p-7 lg:border-b-0 lg:border-r md:p-9" data-id="element-394" data-mp="frf1fm3bmipa">
          <active.icon className="h-7 w-7 text-ct-deep" aria-hidden data-id="element-395" data-mp="kooq2zivyqc2" />
          <h3 className="mt-5 font-display text-[27px] font-black text-navy" data-id="element-396" data-mp="pi9outsazocb">{active.label}</h3>
          <p className="mt-3 text-[15px] leading-relaxed text-slate" data-id="element-397" data-mp="ugb93i1gttpd">{active.intro}</p>
          <a href="https://ctgroup.com.au/partnerships-software/" className="mt-6 inline-flex items-center gap-2 self-start text-[13px] font-bold text-ct-deep" data-id="element-398" data-mp="3szrx32bzmxd9">Explore software experience <ArrowUpRight className="h-4 w-4" aria-hidden data-id="element-399" data-mp="6qql2kqgg698" /></a>
        </div>
        <dl className="grid sm:grid-cols-2 xl:grid-cols-5" data-id="element-400" data-mp="asvh1q4dlez1">
          {active.items.map(([name, purpose]) => <div key={name} className="flex min-h-[150px] flex-col justify-end border-b border-border p-5 sm:border-r xl:border-b-0" data-id="element-401" data-mp="1i6fadir6lhd7"><dt className="font-display text-[17px] font-black text-navy" data-id="element-402" data-mp="1m7has2wu3zgr">{name}</dt><dd className="mt-2 text-[12px] leading-relaxed text-slate" data-id="element-403" data-mp="1m9v3i541kzl5">{purpose}</dd></div>)}
        </dl>
      </div>
    </Section>;
}